/**
 * Test mode for doors / windows, as pure functions: the draft drawn in an
 * opening of a test wall — straight or curved, any thickness, either hinge
 * and side — through the same pipeline as a placed opening sketch
 * (openingGlyphs.ts → openingWallMap.ts), so the bend along a curved wall and
 * the width scaling look exactly as they will in a project.
 *
 * Canvas frame: the opening centered on the origin, the wall centerline on
 * n = 0 at the opening's middle (+u right, +n up).
 */

import { DEFAULT_OPENING_WALL_THICKNESS, DoorType, OpeningKind, STANDARD_WINDOW_TYPE, type ObjectDefinitionSpec, type ObjectShapeLayer, type Vec2 } from 'shared-types';
import type { OpeningFrame } from '../../../potree/utils/FloorMesh3DBuilder';
import { buildCustomOpeningSymbol } from '../../../potree/utils/skeleton/openingGlyphs';
import { openingLocalOf, openingPointAt } from '../../../potree/utils/skeleton/openingWallMap';
import { resolveCustomParams } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { testSize, type TestInstance } from '../EditorContext';

/** How far the test wall runs past each jamb (m). */
const WALL_STUB = 0.8;
/** Smallest opening a jamb drag leaves (m). */
export const MIN_TEST_OPENING = 0.1;
/** Radii below this count as straight (m) — also the tightest bend offered. */
export const MIN_TEST_WALL_RADIUS = 0.5;

export const testWallThickness = (test: TestInstance): number => test.wall ?? DEFAULT_OPENING_WALL_THICKNESS;

/** The test opening's frame: symbol line on the wall centerline, bent when the test wall has a radius. */
export function testOpeningFrame(spec: ObjectDefinitionSpec, test: TestInstance): OpeningFrame {
  const { width } = testSize(test, spec);
  const radius = test.wallRadius ?? 0;
  const frame: OpeningFrame = { center: { x: 0, y: 0 }, wallDir: { x: 1, y: 0 }, wallNorm: { x: 0, y: 1 }, halfWidth: width / 2 };
  return Math.abs(radius) >= MIN_TEST_WALL_RADIUS ? { ...frame, bend: { curvature: 1 / radius, perpOffset: 0 } } : frame;
}

/** The draft's symbol in the test opening (flat `[x,y,0, …]` canvas segments). */
export function testOpeningGlyph(spec: ObjectDefinitionSpec, test: TestInstance, layer: ObjectShapeLayer = 'glyph', dashedOut?: number[]): number[] {
  const { height } = testSize(test, spec);
  const kind = spec.opening?.kind === 'WINDOW' ? OpeningKind.WINDOW : OpeningKind.DOOR;
  return buildCustomOpeningSymbol(
    spec,
    {
      frame: testOpeningFrame(spec, test),
      kind,
      wallThickness: testWallThickness(test),
      hingeAtStart: !test.hingeAtEnd,
      swingPositive: !test.otherSide,
      doorType: DoorType.SINGLE,
      windowType: STANDARD_WINDOW_TYPE,
      rollable: false,
      height,
      customParams: resolveCustomParams(spec, test.params),
    },
    0,
    layer,
    dashedOut,
  );
}

/** A band of the wall between `u0` and `u1` along the centerline, as a closed outline (canvas coordinates). */
function wallBand(frame: OpeningFrame, u0: number, u1: number, thickness: number): Vec2[] {
  const steps = Math.max(1, Math.ceil(Math.abs(u1 - u0) / 0.05));
  const side = (n: number, from: number, to: number): Vec2[] =>
    Array.from({ length: steps + 1 }, (_, i) => {
      const p = openingPointAt(frame, from + ((to - from) * i) / steps, n);
      return [p.x, p.y];
    });
  return [...side(-thickness / 2, u0, u1), ...side(thickness / 2, u1, u0)];
}

/** The test wall: the two pieces left and right of the opening (closed outlines). */
export function testWallPieces(spec: ObjectDefinitionSpec, test: TestInstance): Vec2[][] {
  const frame = testOpeningFrame(spec, test);
  const t = testWallThickness(test);
  const hw = frame.halfWidth;
  return [wallBand(frame, -hw - WALL_STUB, -hw, t), wallBand(frame, hw, hw + WALL_STUB, t)];
}

/** The jamb handles (canvas positions) — dragging one sets the width, the opening stays centered. */
export function testOpeningHandles(spec: ObjectDefinitionSpec, test: TestInstance): Array<{ end: 'start' | 'end'; u: number; n: number }> {
  const frame = testOpeningFrame(spec, test);
  return (['start', 'end'] as const).map((end) => {
    const p = openingPointAt(frame, end === 'start' ? -frame.halfWidth : frame.halfWidth, 0);
    return { end, u: p.x, n: p.y };
  });
}

/** Drag a jamb to the canvas point `p`: the width follows symmetrically (like resizing about the center). */
export function dragTestOpeningJamb(spec: ObjectDefinitionSpec, test: TestInstance, p: Vec2): TestInstance {
  const { u } = openingLocalOf(testOpeningFrame(spec, test), { x: p[0], y: p[1] });
  return { ...test, width: Math.max(MIN_TEST_OPENING, 2 * Math.abs(u)) };
}

/** Radii beyond this read as a straight wall (m). */
const MAX_TEST_WALL_RADIUS = 100;

/** The wall-bend handle: on the wall centerline at the far end of the right-hand wall piece. */
export function testWallBendHandle(spec: ObjectDefinitionSpec, test: TestInstance): Vec2 {
  const frame = testOpeningFrame(spec, test);
  const p = openingPointAt(frame, frame.halfWidth + WALL_STUB, 0);
  return [p.x, p.y];
}

/**
 * Drag the wall-bend handle to the canvas point `p`: the wall becomes the
 * circle through the opening's center (tangent to the wall there) and `p` —
 * R = (x² + y²) / 2y. Nearly straight snaps to straight; tighter than the
 * smallest radius stops there.
 */
export function dragTestWallBend(test: TestInstance, p: Vec2): TestInstance {
  const [x, y] = p;
  if (Math.abs(y) < 1e-6) return { ...test, wallRadius: 0 };
  const radius = (x * x + y * y) / (2 * y);
  if (Math.abs(radius) > MAX_TEST_WALL_RADIUS) return { ...test, wallRadius: 0 };
  return { ...test, wallRadius: Math.sign(radius) * Math.max(MIN_TEST_WALL_RADIUS, Math.abs(radius)) };
}

/** The drawing's hinge (−u jamb) and opening side (+n) markers at the default size, for Edit mode. */
export function openingEditGuides(spec: ObjectDefinitionSpec): { wall: Vec2[][]; hinge: Vec2; side: Vec2 } {
  const hw = spec.defaultSize.width / 2;
  const t = DEFAULT_OPENING_WALL_THICKNESS;
  const frame: OpeningFrame = { center: { x: 0, y: 0 }, wallDir: { x: 1, y: 0 }, wallNorm: { x: 0, y: 1 }, halfWidth: hw };
  return { wall: [wallBand(frame, -hw - WALL_STUB, -hw, t), wallBand(frame, hw, hw + WALL_STUB, t)], hinge: [-hw, 0], side: [0, t / 2] };
}
